import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  FaCheckCircle, FaRegCircle,
  FaUsers, FaLink, FaUserShield, FaChartLine
} from 'react-icons/fa';
import axios from 'axios';
import { useAuth } from '../components/AuthContext';
import { useNavigate } from 'react-router-dom';
import { validateCPF } from '../components/utils';
import './Onboarding.css';
import NicheSelection from '../components/NicheSelection';

const API = process.env.REACT_APP_API_URL || 'http://localhost:4000';

const steps = [
  { icon: <FaUsers />, title: 'Adicionar grupo', desc: 'Cole o link do seu grupo.' },
  { icon: <FaLink />, title: 'Conectar bot', desc: 'Enviamos o bot e você o torna admin.' },
  { icon: <FaUserShield />, title: 'Configurar feed', desc: 'Escolha o feed de ofertas.' },
  { icon: <FaChartLine />, title: 'Pronto pra vender!', desc: 'Acompanhe tudo no dashboard.' },
];

export default function Onboarding() {
  const { user, updateStage, updateUser, logout } = useAuth();
  const navigate = useNavigate();

  /* —— headers + handler de auth —— */
  const authHeaders = useMemo(
    () => ({ Authorization: 'Bearer ' + (user?.token || '') }),
    [user?.token]
  );

  const handleAuthError = useCallback((err) => {
    const status = err?.response?.status;
    const code = err?.response?.data?.code;
    if (status === 401 || code === 'USER_NOT_FOUND' || code === 'INVALID_TOKEN') {
      try { alert('Sua sessão expirou. Faça login novamente.'); } catch {}
      logout();
      navigate('/', { replace: true });
      return true;
    }
    return false;
  }, [logout, navigate]);

  /* —— estados gerais —— */
  const [groupLink, setGroupLink] = useState('');
  const [active, setActive] = useState(user?.onboardStage ?? 0);
  const [waiting, setWaiting] = useState(false);
  const [waitingAdmin, setWA] = useState(false);
  const [errMsg, setErrMsg] = useState('');
  const [groupName, setGroupName] = useState(user?.groupName ?? '');
  const [selectedFeeds, setSelectedFeeds] = useState([]);
  const [selectedNiches, setSelectedNiches] = useState([]);

  /* —— pagamento —— */
  const [cpf, setCpf] = useState('');
  const [pixKey, setPixKey] = useState('');
  const [contactWhatsapp, setContactWhatsapp] = useState('');
  const [isCpfValid, setIsCpfValid] = useState(true);

  /* —— sync inicial —— */
  useEffect(() => {
    if (user?.onboardStage >= 4) {
      navigate('/dashboard');
    }
    const currentStage = user?.onboardStage ?? 0;
    setActive(currentStage);
    setGroupName(user?.groupName ?? '');
    setWA(currentStage === 1);
    if (user?.subscribedFeeds) setSelectedFeeds(user.subscribedFeeds);
    if (user?.selectedNiches) setSelectedNiches(user.selectedNiches);
  }, [user, navigate]);

  useEffect(() => {
    if (active === 2 && selectedFeeds.length === 0) {
      setSelectedFeeds(['Shopee']);
    }
  }, [active, selectedFeeds]);

  useEffect(() => {
    const rawCpf = cpf.replace(/\D/g, '');
    if (rawCpf.length === 11) setIsCpfValid(validateCPF(rawCpf));
    else if (rawCpf.length > 0) setIsCpfValid(false);
    else setIsCpfValid(true);
  }, [cpf]);

  const handleNicheChange = (niches) => {
    setSelectedNiches(niches);
  };

  /* —— persistência de stage (com 401 guard) —— */
  const persistStage = useCallback(async (stage) => {
    try {
      await axios.put(`${API}/api/onboarding/stage`, { stage }, { headers: authHeaders });
      updateStage(stage);
    } catch (e) {
      if (handleAuthError(e)) return;
      console.warn('Falha ao salvar stage', e?.response?.data || e.message);
    }
  }, [authHeaders, updateStage, handleAuthError]);

  const handleFeedToggle = (feedName) => {
    setErrMsg('');
    setSelectedFeeds(prev => prev.includes(feedName) ? prev.filter(f => f !== feedName) : [...prev, feedName]);
  };

  const formatCPF = (value) => value.replace(/\D/g, '').replace(/(\d{3})(\d)/, '$1.$2').replace(/(\d{3})(\d)/, '$1.$2').replace(/(\d{3})(\d{1,2})/, '$1-$2').substring(0, 14);
  const formatWhatsapp = (value) => value.replace(/\D/g, '').replace(/(\d{2})(\d)/, '($1) $2').replace(/(\d{5})(\d)/, '$1-$2').substring(0, 15);

  /* —— polling: bot entrou no grupo —— */
  useEffect(() => {
    if (!waiting) return;
    setErrMsg('');
    const id = setInterval(async () => {
      try {
        const { data } = await axios.get(`${API}/api/onboard-status`, { headers: authHeaders });
        if (data.error) {
          clearInterval(id);
          setWaiting(false);
          if (data.error === 'not-authorized') setErrMsg('O bot foi expulso deste grupo e não pode reentrar.');
          else setErrMsg('O link do convite parece ser inválido ou expirou.');
          return;
        }
        if (data.stage >= 1) {
          clearInterval(id);
          setWaiting(false);
          await persistStage(1);
        }
      } catch (err) {
        if (handleAuthError(err)) {
          clearInterval(id);
          setWaiting(false);
          return;
        }
        console.error('Erro no polling de entrada no grupo:', err);
        setErrMsg('Erro de comunicação. Tente novamente mais tarde.');
        setWaiting(false);
        clearInterval(id);
      }
    }, 5000);
    return () => clearInterval(id);
  }, [waiting, persistStage, authHeaders, handleAuthError]);

  /* —— polling: promoção para admin (se usar) —— */
  useEffect(() => {
    if (!waitingAdmin) return;
    const id = setInterval(async () => {
      try {
        const { data } = await axios.get(`${API}/api/onboard-status`, { headers: authHeaders });
        if (data.stage < active) {
          updateStage(data.stage);
        } else if (data.stage >= 2) {
          clearInterval(id);
          await persistStage(2);
        }
      } catch (err) {
        if (handleAuthError(err)) {
          clearInterval(id);
          return;
        }
        console.error('Erro no polling de promoção admin:', err);
      }
    }, 5000);
    return () => clearInterval(id);
  }, [waitingAdmin, persistStage, authHeaders, active, updateStage, handleAuthError]);

  /* —— next() com 401 guard —— */
  async function next() {
    setErrMsg('');
    if (active === 0) {
      if (!groupLink.trim()) { setErrMsg('Cole o link do grupo!'); return; }
      try {
        await axios.post(`${API}/api/group-link`, { link: groupLink.trim() }, { headers: authHeaders });
        setWaiting(true);
      } catch (e) {
        if (handleAuthError(e)) return;
        const msg = e?.response?.data?.message || 'Falha ao conectar o bot.';
        setErrMsg(msg);
      }
      return;
    }
    if (active === 1) {
      if (waitingAdmin) return;
      await persistStage(2);
      return;
    }
    if (active === 2) {
      if (selectedFeeds.length === 0) {
        setErrMsg('Você precisa selecionar um feed.');
        return;
      }
      try {
        await axios.put(`${API}/api/user/feeds`, { feeds: selectedFeeds, niches: selectedNiches }, { headers: authHeaders });
        // Atualiza o contexto do usuário com as novas preferências
        const { data: updatedUser } = await axios.get(`${API}/api/me`, { headers: authHeaders });
        updateUser(updatedUser);
        await persistStage(3);
      } catch (error) {
        if (handleAuthError(error)) return;
        console.error("Erro ao salvar feeds:", error);
        setErrMsg('Não foi possível salvar sua escolha.');
      }
      return;
    }
    if (active === 3) {
      if (!isCpfValid || !pixKey || !contactWhatsapp) {
        return setErrMsg('Por favor, preencha todos os campos corretamente.');
      }
      try {
        await axios.put(
          `${API}/api/user/payout-info`,
          { cpf: cpf.replace(/\D/g, ''), pixKey, contactWhatsapp: contactWhatsapp.replace(/\D/g, '') },
          { headers: authHeaders }
        );
        await persistStage(4);
        navigate('/dashboard');
      } catch (error) {
        if (handleAuthError(error)) return;
        setErrMsg(error.response?.data?.message || 'Não foi possível salvar seus dados.');
      }
      return;
    }
  }

  // Evita renderizar conteúdo inútil enquanto redireciona
  if (active >= steps.length) return null;

  /* —— render —— */
  return (
    <div className="onboard-wrap">
      <aside className="timeline">
        <ul>
          {steps.map((s, i) => {
            const isDoneForIcon = i < active || (i === 0 && waiting);
            const showLine = i < active;
            return (
              <li key={i} className={`${isDoneForIcon ? 'done' : ''} ${showLine ? 'has-line' : ''}`}>
                <span className="bullet">{isDoneForIcon ? <FaCheckCircle /> : <FaRegCircle />}</span>
                <div className="text"><strong>{s.title}</strong><small>{s.desc}</small></div>
              </li>
            );
          })}
        </ul>
        <button className="btn-logout" onClick={logout}>Sair</button>
      </aside>

      <section className="step-card">
        <header>
          <img src="/logo192.png" alt="logo" height="32" className={waiting || waitingAdmin ? 'logo-spin' : ''} />
          <h2>{steps[active].title}</h2>
          <p>{steps[active].desc}</p>
        </header>

        {active === 0 && (
          <>
            <form onSubmit={e => { e.preventDefault(); next(); }}>
              <input placeholder="https://chat.whatsapp.com/..." value={groupLink} onChange={e => setGroupLink(e.target.value)} disabled={waiting} />
              <button type="submit" disabled={waiting}>{waiting ? 'Enviando…' : 'Continuar'}</button>
            </form>
            {errMsg && <small className="error">{errMsg}</small>}
          </>
        )}

        {active === 1 && (
          <>
            <div className="status-checklist">
              <div className="status-item done"><FaCheckCircle /><span>Bot entrou no grupo <strong>{groupName}</strong>.</span></div>
              <div className={`status-item ${waitingAdmin ? 'pending' : 'done'}`}>{waitingAdmin ? <FaRegCircle /> : <FaCheckCircle />}<span>Dê permissão de admin ao nosso número.</span></div>
            </div>
            {waitingAdmin && (<p className="waiting-text">Aguardando promoção para continuar...</p>)}
            <button onClick={next} disabled={waitingAdmin}> {waitingAdmin ? 'Aguardando…' : 'Continuar'} </button>
          </>
        )}

        {active === 2 && (
          <>
            <div className="feed-selection-container">
              <div className={`feed-card ${selectedFeeds.includes('Shopee') ? 'selected' : ''}`} onClick={() => handleFeedToggle('Shopee')}>
                <img src="/shopee.svg" alt="Shopee" className="feed-logo" />
                <h3>Promoções Shopee</h3>
                <p>Receba as melhores ofertas e cupons da Shopee.</p>
                <div className="checkbox-icon">{selectedFeeds.includes('Shopee') ? <FaCheckCircle /> : <FaRegCircle />}</div>
              </div>
              <div className="feed-card disabled">
                <img src="/amazon.svg" alt="Amazon" className="feed-logo" />
                <h3>Promoções Amazon</h3>
                <p>As melhores ofertas do dia da Amazon no seu grupo.</p>
                <span className="soon-badge">Em Breve</span>
              </div>
            </div>
            
            {selectedFeeds.includes('Shopee') && (
              <NicheSelection
                selectedNiches={selectedNiches}
                onNicheChange={handleNicheChange}
                isOnboarding={true}
              />
            )}

            {errMsg && <small className="error" style={{ textAlign: 'center', width: '100%' }}>{errMsg}</small>}
            <button onClick={next} disabled={selectedFeeds.length === 0}>Continuar</button>
          </>
        )}

        {active === 3 && (
          <>
            <form className="payout-form" onSubmit={e => { e.preventDefault(); next(); }}>
              <p className="info">Para finalizar, precisamos dos seus dados para o repasse das comissões. Suas informações estão seguras conosco.</p>
              <div className="form-group">
                <label htmlFor="cpf">Seu CPF</label>
                <input id="cpf" type="text" placeholder="000.000.000-00" value={cpf} onChange={(e) => setCpf(formatCPF(e.target.value))} className={!isCpfValid && cpf.length > 0 ? 'input-error' : ''} required />
                {!isCpfValid && cpf.replace(/\D/g, '').length === 11 && <small className="error-text">CPF inválido</small>}
              </div>
              <div className="form-group">
                <label htmlFor="pixKey">Sua Chave PIX</label>
                <input id="pixKey" type="text" placeholder="Email, telefone, CPF/CNPJ ou chave aleatória" value={pixKey} onChange={(e) => setPixKey(e.target.value)} required />
              </div>
              <div className="form-group">
                <label htmlFor="contactWhatsapp">WhatsApp de Contato</label>
                <input id="contactWhatsapp" type="tel" placeholder="(XX) XXXXX-XXXX" value={contactWhatsapp} onChange={(e) => setContactWhatsapp(formatWhatsapp(e.target.value))} required />
              </div>
            </form>
            {errMsg && <small className="error">{errMsg}</small>}
            <button onClick={next}>Concluir e ir para o Dashboard</button>
          </>
        )}
      </section>
    </div>
  );
}